import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Card, Modal } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faPen, faTrash, faFilter, faSync,
    faUserTag, faBuilding, faSitemap, faEye
} from '@fortawesome/free-solid-svg-icons';
import PaginatedItems from '../../../../components/administracao/Pagination';
import RequireRoles from '../../../../components/RequireRoles';
import useFetch from '../../../../hooks/useFetch';
import Swal from 'sweetalert2';

export default function GerenciarPessoas() {
    const { params } = useParams();
    const { get, del } = useFetch();

    const defaultState = {
        pessoas: [],
        count: 0,
        filtros: { nome: "", instituicao: "" },
        filtroAtivo: false,
        loading: true,
        mostrarModal: false,
        registroAExcluir: {}
    };

    const [state, setState] = useState(() => defaultState);

    useEffect(() => {
        async function initialize() {
            let [pessoas, count] = await buscarPessoas({});
            setState(s => ({ ...s, pessoas, count, loading: false }));
        }
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    async function buscarPessoas(filtros) {
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") query = query.concat(key, "=", filtros[key], "&");
            }
        }
        try {
            let response = await get('/pessoaretratada' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            }
            return [[], 0];
        } catch (error) {
            console.error('Erro no buscarPessoas():', error);
            return [[], 0];
        }
    }

    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtros = state.filtroAtivo ? { ...state.filtros, page: pageNumber } : { page: pageNumber };
        let [pessoas, count] = await buscarPessoas(filtros);
        setState({ ...state, pessoas, count, loading: false });
    }

    const filtrarLocal = async () => {
        let [pessoas, count] = await buscarPessoas(state.filtros);
        setState(s => ({ ...s, pessoas, count, filtroAtivo: true, loading: false }));
    };

    const limparFiltros = async () => {
        let [pessoas, count] = await buscarPessoas({});
        setState(s => ({ ...s, filtros: defaultState.filtros, pessoas, count, filtroAtivo: false, loading: false }));
    };

    async function excluirPessoa() {
        try {
            let response = await del('/pessoaretratada/' + state.registroAExcluir.id_pessoa);
            if (response.status === 200) {
                let [pessoas, count] = await buscarPessoas(state.filtroAtivo ? state.filtros : {});
                setState({ ...state, pessoas, count, mostrarModal: false, registroAExcluir: {}, loading: false });
            } else if (response.status === 409) {
                setState({ ...state, mostrarModal: false });
                Swal.fire({ title: "Não foi possível excluir", text: response.result?.message ?? "", icon: "error", confirmButtonColor: "#587b7f" });
            } else {
                console.error('Erro no excluirPessoa()');
                Swal.fire({ title: "Não foi possível excluir o registro", text: "Erro inesperado, tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no excluirPessoa():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Pessoas Retratadas</h2>
                    <p className="admin-subtitle">Gerencie as personalidades e figuras identificadas nas fotografias</p>
                </div>
                <Link to="/admin/iconografico/pessoas/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Nova Pessoa
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    <Form className="row g-3" onSubmit={(e) => { e.preventDefault(); filtrarLocal(); }}>
                        <Form.Group className="col-md-6">
                            <Form.Label className="label-admin">Nome da Pessoa</Form.Label>
                            <Form.Control
                                className="input-moderno"
                                placeholder="Buscar por nome..."
                                value={state.filtros.nome}
                                onChange={e => setState({ ...state, filtros: { ...state.filtros, nome: e.target.value } })}
                            />
                        </Form.Group>
                        <Form.Group className="col-md-4">
                            <Form.Label className="label-admin">Instituição</Form.Label>
                            <Form.Control
                                className="input-moderno"
                                placeholder="Ex: FMRP, USP..."
                                value={state.filtros.instituicao}
                                onChange={e => setState({ ...state, filtros: { ...state.filtros, instituicao: e.target.value } })}
                            />
                        </Form.Group>
                        <div className="col-md-2 d-flex gap-2 align-items-end">
                            <Button type="button" className="btn-apply-filters w-100" onClick={filtrarLocal}>
                                <FontAwesomeIcon icon={faFilter} />
                            </Button>
                            <Button type="button" className="btn-clear-filters" onClick={limparFiltros}>
                                <FontAwesomeIcon icon={faSync} />
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Cargo / Função</th>
                                <th>Instituição</th>
                                <th>Departamento</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.pessoas.length > 0 ? (
                                state.pessoas.map((item) => (
                                    <tr key={item.id_pessoa}>
                                        <td data-label="Nome" className="fw-bold">
                                            <FontAwesomeIcon icon={faUserTag} className="me-2 text-muted" />
                                            {item.nome}
                                        </td>
                                        <td data-label="Cargo / Função" className="text-muted">{item.cargo_funcao || '-'}</td>
                                        <td data-label="Instituição">
                                            <small className="badge bg-light text-dark border">
                                                <FontAwesomeIcon icon={faBuilding} className="me-1 text-teal" />
                                                {item.instituicao || 'N/A'}
                                            </small>
                                        </td>
                                        <td data-label="Departamento" className="text-muted">
                                            <FontAwesomeIcon icon={faSitemap} className="me-1 small opacity-50" />
                                            {item.departamento || '-'}
                                        </td>
                                        <td data-label="Opções">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Link className="action-icon view" to={`/admin/iconografico/pessoas/${item.id_pessoa}`} state={{ acao: "Visualizar" }}><FontAwesomeIcon icon={faEye} /></Link>
                                                <Link className="action-icon edit" to={`/admin/iconografico/pessoas/${item.id_pessoa}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                                <RequireRoles allowedRoles={['Desenvolvedor', 'Administrador']}>
                                                    <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: item })}><FontAwesomeIcon icon={faTrash} /></button>
                                                </RequireRoles>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="text-center py-4 text-muted">Nenhum registro encontrado.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.pessoas.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Registro</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja remover <strong>{state.registroAExcluir.nome}</strong> da lista de pessoas retratadas?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirPessoa}>Confirmar Remoção</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
